import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { Float, Plane, Sphere, useGLTF, useTexture } from '@react-three/drei'
import { RigidBody, MeshCollider, CuboidCollider, quat, Physics } from "@react-three/rapier";
import { useFrame } from '@react-three/fiber';
import { lerp } from 'three/src/math/MathUtils.js';
import { VuseModel } from './Vuse';
import { useWakeLock } from 'react-screen-wake-lock';
import { VuseGoModel } from './VuseGo';

import Polystyrene_ao_1k from  "./textures/polystyrene_ao_1k.jpg"
import Polystyrene_diff_1k from  "./textures/polystyrene_diff_1k.jpg"
import Polystyrene_nor_gl_1k from  "./textures/polystyrene_nor_gl_1k.jpg"
import { GloModel } from './Glo';

export function MazeGame(props) {
    const { nodes, materials } = useGLTF('/data/mase_2.gltf')
    const ballRef = useRef(null)
    const mazeRef = useRef(null)
    const intersect = useRef(null)
    const [isect, setIsect] = useState(null)

    const { isSupported, released, request, release } = useWakeLock();

    useEffect(() => {
        if(isSupported) request()
    }, [])

    useEffect(() => {
        if(isect !== null) props.setWin(isect)
    }, [isect])

    const isMobile = () => {
        let check = false;
        (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
        return check;
    };

    const impulse = new THREE.Vector3();

    const textureProps = useTexture({
        map: Polystyrene_diff_1k,
        normalMap: Polystyrene_nor_gl_1k,
        aoMap: Polystyrene_ao_1k,
    })

    useFrame(({ mouse }) => {
        if (mazeRef.current && !isMobile() && props.accept  && !impulse.current) {
            const currentQuateranion = mazeRef.current.rotation
            const quaternion = quat({x: lerp(currentQuateranion.x, -mouse.y/5, 0.06), y: 0, z: lerp(currentQuateranion.z, -mouse.x/5, 0.06)});

            mazeRef.current.rotation.x = quaternion.x
            mazeRef.current.rotation.y = quaternion.y
            mazeRef.current.rotation.z = quaternion.z

            impulse.x = mouse.x/80;
            impulse.y = 0;
            impulse.z = -mouse.y/80;
            ballRef.current.applyImpulse(impulse, true);
        }
    });

    const cameraProperties = useMemo(
        () => ({
            position: isMobile() ? new THREE.Vector3(0, 35, 17) : new THREE.Vector3(0, 15, 15),
            target: new THREE.Vector3()
        }),
        []
    )

    const onDeviceMotion = (event) => {
        let ax;
        let ay;

        switch (screen.orientation.type) {
            case "portrait-primary":
                ax = event.gamma;
                ay = -event.beta;
                break;
            case "landscape-primary":
                ax = event.beta;
                ay = event.gamma;
                break;
            case "landscape-secondary":
                ax = -event.beta;
                ay = -event.gamma;
                break;
            case "portrait-secondary":
                ax = -event.gamma;
                ay = event.beta;
                break;
        }

        if (ballRef.current && mazeRef.current && !impulse.current) {
            const currentQuateranion = mazeRef.current.rotation
            const quaternion = quat({x: lerp(currentQuateranion.x, -ay/100, 0.06), y: 0, z: lerp(currentQuateranion.z, -ax/100, 0.06)});

            mazeRef.current.rotation.x = quaternion.x
            mazeRef.current.rotation.y = quaternion.y
            mazeRef.current.rotation.z = quaternion.z

            impulse.x = ax/2000;
            impulse.y = 0;
            impulse.z = -ay/2000;
            ballRef.current.applyImpulse(impulse, true);
        }
    }

    useEffect(() => {
        if(isMobile() && props.accept) { 
            window.addEventListener('deviceorientation', onDeviceMotion) 
        }
    }, [props.accept])

    useFrame(({ camera, mouse }, delta) => {
        if(props.accept) {
            if(intersect.current) {
                let ballPosition = {x: 0, y: 0, z: -1}

                const cameraPosition = new THREE.Vector3()
                cameraPosition.x = 0
                cameraPosition.z = 5
                cameraPosition.y = 5

                const cameraTarget = new THREE.Vector3()
                cameraTarget.copy(ballPosition)
                cameraTarget.y = 0

                cameraProperties.position.lerp(cameraPosition, 2 * delta)
                cameraProperties.target.lerp(cameraTarget, 2 * delta)

                camera.position.copy(cameraProperties.position)
                camera.lookAt(cameraProperties.target)
            } else {
                let ballPosition = ballRef.current.translation()

                const cameraPosition = new THREE.Vector3()
                cameraPosition.copy(ballPosition)
                cameraPosition.z += 5
                cameraPosition.y = isMobile() ? 16 : 7

                const cameraTarget = new THREE.Vector3()
                cameraTarget.copy(ballPosition)
                cameraTarget.y = 0

                cameraProperties.position.lerp(cameraPosition, 2 * delta)
                cameraProperties.target.lerp(cameraTarget, 2 * delta)

                camera.position.copy(cameraProperties.position)
                camera.lookAt(cameraProperties.target)
            }
        }
    })

    return (
        <Physics colliders={false} gravity={[0, -9.81, 0]}>
            <group ref={mazeRef} {...props} dispose={null} rotation={[0, 0, 0]} >
                <RigidBody type="fixed" 
                restitution={0.5} friction={5}>
                    <MeshCollider type="trimesh">
                        <group position={[0, -1.6, 0]} scale={[8, 7.2, 7]}>
                            <mesh castShadow receiveShadow geometry={nodes.Plane.geometry} material={materials.floor} />
                            <mesh
                            castShadow
                            receiveShadow
                            geometry={nodes.Plane_1.geometry}
                            material={materials.sciany}
                            />
                            <mesh
                            castShadow
                            receiveShadow
                            geometry={nodes.Plane_2.geometry}
                            material={materials.material1}
                            />
                        </group>
                        <Plane args={[100 ,100, 1]} rotation={[-Math.PI/2, 0, 0]} position={[0, -1.6, 0]} castShadow receiveShadow>
                            <meshPhysicalMaterial transparent opacity={0}/>
                        </Plane>
                        <Plane args={[300 ,300, 1]} rotation={[-Math.PI/2, 0, 0]} position={[0, -0.95, 0]} castShadow receiveShadow>
                            <meshPhysicalMaterial transparent opacity={0}/>
                        </Plane>
                    </MeshCollider>
                </RigidBody>
                <VuseModel visible={!isect} position={[7.2, -1.6, -5.9]}/>
                <VuseGoModel visible={!isect} position={[-8.2, -1.3, -5.4]} scale={1}/>
                <GloModel visible={!isect} position={[-4.8, -1.6, 4]} scale={0.3}/>
                <Float floatIntensity={2} rotationIntensity={2}>
                    <VuseModel visible={isect === "vuse"} position={[0, 0, 0]}/>
                    <VuseGoModel visible={isect === "vusego"} position={[0, 0.5, 0]} scale={0.7}/>
                    <GloModel visible={isect === "glo"} position={[0, 0, 0]} scale={0.3}/>
                </Float>
                <RigidBody type="fixed">
                    <CuboidCollider
                        position={[7, -1.6, -5.8]}
                        rotation={[0, Math.PI/4.5, 0]}
                        args={[0.5, 2, 0.5]}
                        sensor
                        onIntersectionEnter={() => {
                            console.log("intersect vuse!")
                            intersect.current = "vuse"
                            setIsect("vuse")
                        }}
                    />
                </RigidBody>
                <RigidBody type="fixed">
                    <CuboidCollider
                        position={[-7.5, -1.6, -5.1]}
                        rotation={[0, Math.PI/3, 0]}
                        args={[0.5, 2, 0.5]}
                        sensor
                        onIntersectionEnter={() => {
                            console.log("intersect vusego!")
                            intersect.current = "vusego"
                            setIsect("vusego")
                        }}
                    />
                </RigidBody>
                <RigidBody type="fixed">
                    <CuboidCollider
                        position={[-4.8, -1.6, 4.1]}
                        rotation={[0, Math.PI/4, 0]}
                        args={[0.5, 2, 0.5]}
                        sensor
                        onIntersectionEnter={() => {
                            console.log("intersect glo!")
                            intersect.current = "glo"
                            setIsect("glo")
                        }}
                    />
                </RigidBody>
                <RigidBody ref={ballRef} colliders="ball"
                    mass={5}
                    restitution={0.3}
                    friction={3}
                    gravityScale={1}
                    position={[-1.7, -1.3, -1.5]}>
                    <Sphere color={[0, 0, 0]} args={[0.25]} position={[0, 0, 0]} castShadow receiveShadow>
                        <meshPhysicalMaterial {...textureProps}/>
                    </Sphere>
                </RigidBody>
            </group>
        </Physics>
    )
}

// // useGLTF.preload('/data/mase_2.gltf')
